import type { DocumentLink } from "@/models/document-link/DocumentLink";
import {
  findMatchRangeInHaystack,
  normalizeForMatchText,
} from "./referenceMatchLocate";
import { findTextMatch } from "./injectDocumentLinks";
import { getEffectiveDivFields } from "./documentLinkDivUtils";
import { isWithinDecoration } from "./uniqueReferenceChunk";

function isUnderAnchor(node: Node, stopAt: Element | null): boolean {
  let p: Node | null = node.parentNode;
  while (p && p !== stopAt) {
    if (p.nodeType === Node.ELEMENT_NODE) {
      if ((p as Element).tagName === "A") return true;
    }
    p = p.parentNode;
  }
  return false;
}

export interface InjectionLinkEntry {
  link: DocumentLink;
  linkId: string;
  anchorId: string;
}

export interface TextNodeInjectionFragment {
  start: number;
  end: number;
  links: InjectionLinkEntry[];
  triggerId: string;
  matchText: string;
}

/**
 * Stable id shared by all split segments of one merged injection (strips trailing `_${seg}` from `triggerId`).
 */
export function docLinkGroupIdFromTriggerId(
  triggerId: string | undefined
): string | undefined {
  if (!triggerId || !triggerId.startsWith("doclink_grp_")) return undefined;
  const m = triggerId.match(/^(.*)_\d+$/);
  return m?.[1];
}

/**
 * True when the fragment would show visible text in the document (NBSP normalized;
 * zero-width / soft hyphen / BOM stripped — those can slip past trim() but look empty).
 */
export function hasVisibleDocumentLinkText(s: string): boolean {
  if (s.length === 0) return false;
  const t = s
    .replace(/\u00a0/g, " ")
    .replace(/[\u00ad\u200b-\u200d\ufeff\u2060]/g, "");
  return t.trim().length > 0;
}

export type DocumentLinkInjectionPlan = Map<
  number,
  TextNodeInjectionFragment[]
>;

/**
 * Ensures we always have a real Map for fragment lookup.
 * Handles: Map instance; `{ plan: Map }` if the full build result was passed by mistake.
 */
export function coerceInjectionPlan(input: unknown): DocumentLinkInjectionPlan {
  if (input instanceof Map) {
    return input as DocumentLinkInjectionPlan;
  }
  if (
    input !== null &&
    typeof input === "object" &&
    "plan" in input &&
    (input as { plan: unknown }).plan instanceof Map
  ) {
    return (input as { plan: DocumentLinkInjectionPlan }).plan;
  }
  return new Map<number, TextNodeInjectionFragment[]>();
}

/** Why a reference from the API was not injected as a clickable fragment */
export type DocumentLinkSkipReason =
  | "missing_from_map"
  | "already_in_html"
  | "no_match_text"
  | "chunk_invalid"
  | "no_primary_html_id"
  | "target_not_in_dom"
  | "no_injectable_text_in_scope"
  | "not_found_in_subtree"
  | "flat_mapping_failed"
  | "flat_out_of_bounds";

export interface DocumentLinkApiSnapshot {
  linkId: string;
  uniqueMatchText: string | undefined;
  uniqueChunkText: string | undefined;
  primaryHtmlId: string | undefined;
}

export interface DocumentLinkInjectionSegmentDebug {
  triggerId: string;
  textNodeIndex: number;
  localStart: number;
  localEnd: number;
  matchText: string;
}

export interface DocumentLinkMergedGroupDebug {
  groupIndex: number;
  merged: boolean;
  linkIds: string[];
  flatStart: number;
  flatEnd: number;
  segments: DocumentLinkInjectionSegmentDebug[];
}

export interface DocumentLinkPerLinkOutcome {
  linkId: string;
  api: DocumentLinkApiSnapshot;
  status: "injected" | "skipped";
  skipReason?: DocumentLinkSkipReason;
  detail?: string;
  /** Present when status === "injected" */
  groupIndex?: number;
  mergedWithOthers?: boolean;
  injectionPath?: DocumentLinkInjectionPath;
}

/** How the match was located relative to `primaryHtmlId` */
export type DocumentLinkInjectionPath =
  | "subtree_haystack"
  | "subtree_haystack_expanded"
  | "per_node_fallback"
  | "per_node_fallback_expanded";

/** One injectable flat range before merge (overlap detection). */
export interface DocumentLinkRawRangeDebug {
  linkId: string;
  flatStart: number;
  flatEnd: number;
  /** Substring of the injectable-only flat text at this range */
  textAtRange: string;
}

export interface DocumentLinkInjectionDebugReport {
  /** Sorted list of unique link ids from the API map */
  apiLinkIds: string[];
  apiSnapshots: DocumentLinkApiSnapshot[];
  perLink: DocumentLinkPerLinkOutcome[];
  mergedGroups: DocumentLinkMergedGroupDebug[];
  /** Injectable text length (flat string used for ranges) */
  injectableFlatLength: number;
  /** Text node count in DOM walk (includes nodes inside <a>) */
  totalTextNodeCount: number;
  /**
   * GUIDs extracted from `<a id="doclink_*">` in the HTML (regex). If a link’s id is
   * listed here, the builder skips it with `already_in_html` — it does not get a
   * raw range and cannot merge with other references.
   */
  preprocessedLinkIdsFromHtml: string[];
  /** Raw `id` attributes on every `a[id^="doclink_"]` in the parsed HTML */
  preprocessedDoclinkAnchorIds: string[];
  /** Injectable ranges computed before merge (one row per link that reached rawRanges) */
  rawRangesBeforeMerge: DocumentLinkRawRangeDebug[];
}

/** Empty report when injection is skipped or build result is unusable. */
export const EMPTY_INJECTION_DEBUG_REPORT: DocumentLinkInjectionDebugReport = {
  apiLinkIds: [],
  apiSnapshots: [],
  perLink: [],
  mergedGroups: [],
  injectableFlatLength: 0,
  totalTextNodeCount: 0,
  preprocessedLinkIdsFromHtml: [],
  preprocessedDoclinkAnchorIds: [],
  rawRangesBeforeMerge: [],
};

export function formatDocumentLinkInjectionReport(
  r: DocumentLinkInjectionDebugReport
): string {
  const lines: string[] = [];
  lines.push("=== Document link injection (plan builder) ===");
  lines.push(
    `injectableFlatLength=${r.injectableFlatLength} totalTextNodes=${r.totalTextNodeCount}`
  );
  lines.push("");
  lines.push("--- API references (deduped by link id) ---");
  for (const s of r.apiSnapshots) {
    lines.push(
      `  ${s.linkId} | htmlId=${s.primaryHtmlId ?? "(none)"} | match=${JSON.stringify(s.uniqueMatchText ?? "")} | chunk=${JSON.stringify(s.uniqueChunkText ?? "")}`
    );
  }
  lines.push("");
  lines.push(
    "--- Preprocessed <a id=doclink_*> in HTML (already_in_html skips) ---"
  );
  lines.push(
    `  extractedGuids=${r.preprocessedLinkIdsFromHtml.join(", ") || "(none)"}`
  );
  for (const id of r.preprocessedDoclinkAnchorIds) {
    lines.push(`  anchor id=${JSON.stringify(id)}`);
  }
  lines.push("");
  lines.push("--- Raw injectable ranges before merge ---");
  for (const row of r.rawRangesBeforeMerge) {
    lines.push(
      `  ${row.linkId} flat=[${row.flatStart}, ${row.flatEnd}) text=${JSON.stringify(row.textAtRange)}`
    );
  }
  lines.push("");
  lines.push("--- Injected (by merged group) ---");
  for (const g of r.mergedGroups) {
    lines.push(
      `  group ${g.groupIndex} merged=${g.merged} links=[${g.linkIds.join(", ")}] flat=[${g.flatStart}, ${g.flatEnd})`
    );
    for (const seg of g.segments) {
      lines.push(
        `    segment ${seg.triggerId} textNode=${seg.textNodeIndex} [${seg.localStart},${seg.localEnd}) ${JSON.stringify(seg.matchText)}`
      );
    }
  }
  lines.push("");
  lines.push("--- Per link: injected vs skipped ---");
  for (const o of r.perLink) {
    if (o.status === "injected") {
      lines.push(
        `  OK ${o.linkId} group=${o.groupIndex} mergedWithOthers=${o.mergedWithOthers} path=${o.injectionPath}${o.detail ? ` (${o.detail})` : ""}`
      );
    } else {
      lines.push(
        `  SKIP ${o.linkId} ${o.skipReason ?? "?"}${o.detail ? ` (${o.detail})` : ""}`
      );
    }
  }
  return lines.join("\n");
}

interface CollectedTextNode {
  node: Text;
  /** Full-document text node index (matches html-react-parser visit order). */
  index: number;
  text: string;
  skipInjection: boolean;
  /** Monotonic DFS position — enables O(1) subtree containment via element intervals. */
  pos: number;
}

interface ElementInterval {
  enter: number;
  exit: number;
}

interface CollectedDom {
  nodes: CollectedTextNode[];
  /**
   * DFS [enter, exit] range per element. A text node is inside the element's subtree
   * iff `enter <= textNode.pos <= exit` — replacing repeated `element.contains(node)`
   * scans (the dominant cost for documents with thousands of links).
   */
  elementInterval: Map<Element, ElementInterval>;
}

function collectTextNodes(doc: Document): CollectedDom {
  const body = doc.body;
  if (!body) return { nodes: [], elementInterval: new Map() };

  const out: CollectedTextNode[] = [];
  const elementInterval = new Map<Element, ElementInterval>();
  let index = 0;
  let dfs = 0;

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node as Text;
      const text = t.data ?? "";
      const skipInjection =
        isUnderAnchor(t, body) || isWithinDecoration(t, body);
      out.push({ node: t, index, text, skipInjection, pos: dfs });
      dfs += 1;
      index += 1;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === "script" || tag === "style") return;
    const enter = dfs;
    dfs += 1;
    for (const child of Array.from(el.childNodes)) {
      visit(child);
    }
    elementInterval.set(el, { enter, exit: dfs });
    dfs += 1;
  };

  visit(body);
  return { nodes: out, elementInterval };
}

/**
 * Text nodes are collected in DFS order, so `allNodes` is sorted ascending by `pos`
 * (and array position === `index`). Returns the inclusive `[lo, hi]` array-index range
 * of text nodes inside `interval`, via binary search, or null when empty.
 */
function scopeIndexRange(
  allNodes: CollectedTextNode[],
  interval: ElementInterval | undefined
): { lo: number; hi: number } | null {
  if (!interval || allNodes.length === 0) return null;
  const { enter, exit } = interval;

  let a = 0;
  let b = allNodes.length - 1;
  let first = -1;
  while (a <= b) {
    const m = (a + b) >> 1;
    if (allNodes[m].pos >= enter) {
      first = m;
      b = m - 1;
    } else {
      a = m + 1;
    }
  }
  if (first === -1) return null;

  a = 0;
  b = allNodes.length - 1;
  let last = -1;
  while (a <= b) {
    const m = (a + b) >> 1;
    if (allNodes[m].pos <= exit) {
      last = m;
      a = m + 1;
    } else {
      b = m - 1;
    }
  }
  if (last === -1 || last < first) return null;
  return { lo: first, hi: last };
}

/** Slice of an ascending-sorted number array whose values fall in `[lo, hi]` (inclusive). */
function sliceSortedInRange(sorted: number[], lo: number, hi: number): number[] {
  if (sorted.length === 0 || lo > hi) return [];

  let a = 0;
  let b = sorted.length - 1;
  let start = sorted.length;
  while (a <= b) {
    const m = (a + b) >> 1;
    if (sorted[m] >= lo) {
      start = m;
      b = m - 1;
    } else {
      a = m + 1;
    }
  }

  a = 0;
  b = sorted.length - 1;
  let end = -1;
  while (a <= b) {
    const m = (a + b) >> 1;
    if (sorted[m] <= hi) {
      end = m;
      a = m + 1;
    } else {
      b = m - 1;
    }
  }

  if (end < start) return [];
  return sorted.slice(start, end + 1);
}

function dedupeLinkEntries(
  entries: InjectionLinkEntry[]
): InjectionLinkEntry[] {
  const seen = new Set<string>();
  const result: InjectionLinkEntry[] = [];
  for (const e of entries) {
    if (seen.has(e.linkId)) continue;
    seen.add(e.linkId);
    result.push(e);
  }
  return result;
}

function linkEntryFor(link: DocumentLink): InjectionLinkEntry {
  return {
    link,
    linkId: link.id,
    anchorId: `doclink_${link.id}_0`,
  };
}

function collectPreprocessedDoclinkDebug(doc: Document): {
  extractedGuids: Set<string>;
  anchorIdAttributes: string[];
} {
  const extractedGuids = new Set<string>();
  const anchorIdAttributes: string[] = [];
  for (const a of doc.querySelectorAll('a[id^="doclink_"]')) {
    const idAttr = a.getAttribute("id");
    if (idAttr) {
      anchorIdAttributes.push(idAttr);
      const m = idAttr.match(/^doclink_([a-f0-9-]+)_\d+$/i);
      if (m) extractedGuids.add(m[1]);
    }
  }
  return { extractedGuids, anchorIdAttributes };
}

/**
 * `primaryHtmlId` may incorrectly store a merged-injection trigger id (`doclink_grp_*`) that
 * only exists after React renders — not in `safeHtml` passed to DOMParser. Extract embedded
 * link UUIDs from that id and resolve a real block element from another link in the map.
 */
function resolveScopeElementForSyntheticPrimaryHtmlId(
  doc: Document,
  targetHtmlId: string,
  documentLinksMap: Map<string, DocumentLink>,
  currentDocumentId?: string
): { element: Element; resolvedFromHtmlId: string; viaLinkId: string } | null {
  const grp = targetHtmlId.match(/^doclink_grp_(.+)_(\d+)_(\d+)$/i);
  if (!grp) return null;
  const sortedIdsPart = grp[1];
  const candidates =
    sortedIdsPart.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
    ) ?? [];
  for (const candidateId of candidates) {
    const other = documentLinksMap.get(candidateId);
    if (!other) continue;
    const eff = getEffectiveDivFields(other, currentDocumentId);
    const hid = eff.htmlIds?.[0];
    if (!hid || hid === targetHtmlId) continue;
    const resolved = getElementByIdWithLegislationIdFallbacks(doc, hid);
    if (resolved) {
      return {
        element: resolved.el,
        resolvedFromHtmlId: hid,
        viaLinkId: candidateId,
      };
    }
  }
  return null;
}

/** UUID shape used in legislation block ids (with or without an `id` prefix in the DOM). */
const UUID_SEGMENT_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeUuidSegment(s: string): boolean {
  return UUID_SEGMENT_RE.test(s.trim());
}

/**
 * `getElementById` plus common legislation mismatches: API stores bare UUID vs DOM `id`+UUID, or the reverse.
 */
function getElementByIdWithLegislationIdFallbacks(
  doc: Document,
  rawId: string
): { el: Element; detail: string } | null {
  const direct = doc.getElementById(rawId);
  if (direct) return { el: direct, detail: "" };

  const tid = rawId.trim();

  if (looksLikeUuidSegment(tid) && !tid.toLowerCase().startsWith("id")) {
    const prefixed = `id${tid}`;
    const el = doc.getElementById(prefixed);
    if (el) {
      return {
        el,
        detail: `getElementById(${JSON.stringify(tid)}) → ${JSON.stringify(prefixed)} (bare UUID → id-prefixed)`,
      };
    }
  }

  if (tid.length > 2 && tid.toLowerCase().startsWith("id")) {
    const rest = tid.slice(2);
    if (looksLikeUuidSegment(rest)) {
      const el = doc.getElementById(rest);
      if (el) {
        return {
          el,
          detail: `getElementById(${JSON.stringify(tid)}) → ${JSON.stringify(rest)} (strip id prefix)`,
        };
      }
    }
  }

  return null;
}

/**
 * Resolve scope element: exact id + legislation id fallbacks, then synthetic `doclink_grp_*` resolution.
 */
function tryResolveScopeElementByHtmlId(
  doc: Document,
  targetHtmlId: string,
  documentLinksMap: Map<string, DocumentLink>,
  currentDocumentId?: string
): { element: Element; detail?: string } | null {
  const byId = getElementByIdWithLegislationIdFallbacks(doc, targetHtmlId);
  if (byId) {
    return {
      element: byId.el,
      ...(byId.detail ? { detail: byId.detail } : {}),
    };
  }

  const synthetic = resolveScopeElementForSyntheticPrimaryHtmlId(
    doc,
    targetHtmlId,
    documentLinksMap,
    currentDocumentId
  );
  if (synthetic) {
    return {
      element: synthetic.element,
      detail: `synthetic primaryHtmlId ${targetHtmlId} → #${synthetic.resolvedFromHtmlId} (via link ${synthetic.viaLinkId})`,
    };
  }

  return null;
}

/**
 * Prefer API chunk when it contains match text (strict, or NBSP + case-insensitive).
 * If not, omit chunk so matching uses {@link uniqueMatchText} only within scope (weaker disambiguation).
 */
function effectiveChunkForInjection(
  uniqueChunkText: string | undefined,
  uniqueMatchText: string
): string | undefined {
  if (!uniqueChunkText) return undefined;
  if (uniqueChunkText.includes(uniqueMatchText)) return uniqueChunkText;
  const c = normalizeForMatchText(uniqueChunkText);
  const m = normalizeForMatchText(uniqueMatchText);
  if (!m) return undefined;
  if (c.toLowerCase().includes(m.toLowerCase())) return uniqueChunkText;
  return undefined;
}

interface RawRange {
  flatStart: number;
  flatEnd: number;
  links: InjectionLinkEntry[];
}

/**
 * Merge ranges that overlap or touch (half-open [a,b) and [b,c) share boundary b).
 * Touching references (e.g. one match ends exactly where the next begins) should still
 * become one merged group so the multi-link popup lists all targets.
 */
function mergeOverlappingRanges(ranges: RawRange[]): RawRange[] {
  const sorted = [...ranges].sort((a, b) => a.flatStart - b.flatStart);
  const out: RawRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (!last || r.flatStart > last.flatEnd) {
      out.push({
        flatStart: r.flatStart,
        flatEnd: r.flatEnd,
        links: dedupeLinkEntries([...r.links]),
      });
    } else {
      last.flatEnd = Math.max(last.flatEnd, r.flatEnd);
      last.links = dedupeLinkEntries([...last.links, ...r.links]);
    }
  }
  return out;
}

function findRangeInSubtreeHaystack(
  subHaystack: string,
  uniqueMatchText: string,
  uniqueChunkText: string | undefined
): { start: number; end: number } | null {
  const direct = findMatchRangeInHaystack(
    subHaystack,
    uniqueMatchText,
    uniqueChunkText || undefined
  );
  if (direct) return direct;

  const m = normalizeForMatchText(uniqueMatchText);
  if (!m) return null;
  const h = normalizeForMatchText(subHaystack);
  let idx = h.indexOf(m);
  if (idx === -1) {
    idx = h.toLowerCase().indexOf(m.toLowerCase());
  }
  if (idx === -1) return null;
  return { start: idx, end: idx + uniqueMatchText.length };
}

function mapSubHaystackRangeToFlat(
  orderedScopeInjectable: number[],
  range: { start: number; end: number },
  flatBaseByGlobalIndex: Map<number, number>,
  allNodes: CollectedTextNode[]
): { flatStart: number; flatEnd: number } | null {
  let pos = 0;
  let flatStart = -1;
  let flatEnd = -1;

  for (const gIdx of orderedScopeInjectable) {
    const entry = allNodes[gIdx];
    const fb = flatBaseByGlobalIndex.get(gIdx);
    if (fb === undefined) return null;
    const len = entry.text.length;
    const segStart = pos;
    const segEnd = pos + len;

    if (range.start >= segStart && range.start < segEnd) {
      flatStart = fb + (range.start - segStart);
    }
    if (range.end > segStart && range.end <= segEnd) {
      flatEnd = fb + (range.end - segStart);
    }
    pos = segEnd;
  }

  if (flatStart === -1 || flatEnd === -1) return null;
  return { flatStart, flatEnd };
}

function findRangeFallbackPerNode(
  allNodes: CollectedTextNode[],
  scopeIndices: Set<number>,
  uniqueMatchText: string,
  uniqueChunkText: string | undefined,
  targetHtmlId: string | undefined,
  flatBaseByGlobalIndex: Map<number, number>
): { flatStart: number; flatEnd: number } | null {
  for (const i of scopeIndices) {
    const item = allNodes[i];
    if (item.skipInjection) continue;
    const text = item.text;

    const directRange = findMatchRangeInHaystack(
      text,
      uniqueMatchText,
      uniqueChunkText || undefined
    );
    let match: { found: boolean; offset: number };

    if (directRange) {
      match = { found: true, offset: directRange.start };
    } else if (uniqueChunkText) {
      let chunkMatch = findTextMatch(text, uniqueChunkText);
      let matchedChunkText = uniqueChunkText;

      if (!chunkMatch.found) {
        const trimmedChunkText = uniqueChunkText.trim();
        if (trimmedChunkText !== uniqueChunkText) {
          const trimmedMatch = findTextMatch(text, trimmedChunkText);
          if (trimmedMatch.found) {
            chunkMatch = trimmedMatch;
            matchedChunkText = trimmedChunkText;
          }
        }
      }

      if (!chunkMatch.found) {
        const isOnTargetDivElement =
          item.node.parentElement?.id === targetHtmlId;
        const shouldUseCrossElementMatching = isOnTargetDivElement;

        if (shouldUseCrossElementMatching) {
          const chunkTextTrimmed = uniqueChunkText.trim();
          let suffixMatch: { found: boolean; offset: number } = {
            found: false,
            offset: -1,
          };
          for (let j = 1; j < chunkTextTrimmed.length; j++) {
            const suffix = chunkTextTrimmed.substring(j);
            const testMatch = findTextMatch(text, suffix);
            if (testMatch.found) {
              suffixMatch = testMatch;
              matchedChunkText = suffix;
              break;
            }
          }
          if (suffixMatch.found) {
            chunkMatch = suffixMatch;
          } else if (!isOnTargetDivElement) {
            const directMatch = findTextMatch(text, uniqueMatchText);
            if (directMatch.found) {
              chunkMatch = directMatch;
              matchedChunkText = uniqueMatchText;
            }
          }
        }
      }

      if (chunkMatch.found) {
        const chunkStart = chunkMatch.offset;
        const chunkEnd = chunkStart + matchedChunkText.length;
        const chunkContext = text.substring(chunkStart, chunkEnd);
        const matchInChunk = chunkContext.indexOf(uniqueMatchText);
        if (matchInChunk !== -1) {
          match = {
            found: true,
            offset: chunkStart + matchInChunk,
          };
        } else {
          match = { found: false, offset: -1 };
        }
      } else {
        match = { found: false, offset: -1 };
      }
    } else {
      match = findTextMatch(text, uniqueMatchText);
    }

    if (match.found) {
      const flatBase = flatBaseByGlobalIndex.get(item.index);
      if (flatBase === undefined) return null;
      return {
        flatStart: flatBase + match.offset,
        flatEnd: flatBase + match.offset + uniqueMatchText.length,
      };
    }
  }
  return null;
}

function describeElementForDebug(el: Element): string {
  const id = el.id?.trim();
  if (id) return `${el.tagName.toLowerCase()}#${id}`;
  return el.tagName.toLowerCase();
}

/**
 * Try to locate match text within injectable text under `searchRoot` (subtree).
 * Returns null if no match, mapping failure, or flat range out of bounds.
 */
function tryFindInSearchRoot(
  searchRoot: Element,
  allNodes: CollectedTextNode[],
  injectableIndices: number[],
  uniqueMatchText: string,
  uniqueChunkText: string | undefined,
  targetHtmlId: string,
  flatBaseByGlobalIndex: Map<number, number>,
  injectableFlatLen: number,
  elementInterval: Map<Element, ElementInterval>
): {
  flatStart: number;
  flatEnd: number;
  path: "subtree_haystack" | "per_node_fallback";
} | null {
  const scopeRange = scopeIndexRange(allNodes, elementInterval.get(searchRoot));
  if (!scopeRange) return null;

  const scopeIndices = new Set<number>();
  for (let i = scopeRange.lo; i <= scopeRange.hi; i += 1) {
    scopeIndices.add(allNodes[i].index);
  }

  const orderedScopeInjectable = sliceSortedInRange(
    injectableIndices,
    scopeRange.lo,
    scopeRange.hi
  );

  if (orderedScopeInjectable.length === 0) {
    return null;
  }

  const subHaystack = orderedScopeInjectable
    .map((idx) => allNodes[idx].text)
    .join("");

  const range = findRangeInSubtreeHaystack(
    subHaystack,
    uniqueMatchText,
    uniqueChunkText || undefined
  );

  if (!range) {
    const fb = findRangeFallbackPerNode(
      allNodes,
      scopeIndices,
      uniqueMatchText,
      uniqueChunkText || undefined,
      targetHtmlId,
      flatBaseByGlobalIndex
    );
    if (!fb) return null;
    const { flatStart, flatEnd } = fb;
    if (flatStart < 0 || flatEnd > injectableFlatLen || flatStart >= flatEnd) {
      return null;
    }
    return { flatStart, flatEnd, path: "per_node_fallback" };
  }

  const mapped = mapSubHaystackRangeToFlat(
    orderedScopeInjectable,
    range,
    flatBaseByGlobalIndex,
    allNodes
  );
  if (!mapped) return null;

  const { flatStart, flatEnd } = mapped;

  if (flatStart < 0 || flatEnd > injectableFlatLen || flatStart >= flatEnd) {
    return null;
  }

  return { flatStart, flatEnd, path: "subtree_haystack" };
}

function toExpandedPath(
  base: "subtree_haystack" | "per_node_fallback"
): DocumentLinkInjectionPath {
  return base === "subtree_haystack"
    ? "subtree_haystack_expanded"
    : "per_node_fallback_expanded";
}

interface InjectableMeta {
  globalTextNodeIndex: number;
  text: string;
  flatBase: number;
}

/** True when the slice should not become a link (empty or only inter-block whitespace). */
function isNonLinkableFragmentSlice(slice: string): boolean {
  return !hasVisibleDocumentLinkText(slice);
}

/**
 * Map a [flatStart, flatEnd) range in injectableFlat to one fragment per text node touched.
 * When the range crosses block boundaries, each contiguous run per block still becomes
 * separate fragments (each text node is its own fragment for React injection).
 */
function flatRangeToFragments(
  flatStart: number,
  flatEnd: number,
  injectableMeta: InjectableMeta[],
  links: InjectionLinkEntry[],
  groupIndex: number
): Array<TextNodeInjectionFragment & { globalTextNodeIndex: number }> {
  if (flatStart >= flatEnd) return [];

  const out: Array<
    TextNodeInjectionFragment & { globalTextNodeIndex: number }
  > = [];
  let seg = 0;
  let pos = flatStart;

  while (pos < flatEnd) {
    const meta = injectableMeta.find(
      (m) => pos >= m.flatBase && pos < m.flatBase + m.text.length
    );
    if (!meta) break;

    const localStart = pos - meta.flatBase;
    const nodeEnd = meta.flatBase + meta.text.length;
    const chunkEnd = Math.min(flatEnd, nodeEnd);
    const localEnd = chunkEnd - meta.flatBase;

    const sliceText = meta.text.substring(localStart, localEnd);
    /* Boundary-only or whitespace-only (e.g. \\n between </p> and <p>) — skip or we'd
       render an empty-looking MultiLinkAnchor and break list layout. */
    if (localStart >= localEnd || isNonLinkableFragmentSlice(sliceText)) {
      pos = chunkEnd;
      continue;
    }

    const sortedIds = [...links]
      .map((l) => l.linkId)
      .sort()
      .join("-");
    const triggerId = `doclink_grp_${sortedIds}_${groupIndex}_${seg}`;
    seg += 1;

    out.push({
      globalTextNodeIndex: meta.globalTextNodeIndex,
      start: localStart,
      end: localEnd,
      links,
      triggerId,
      matchText: sliceText,
    });

    pos = chunkEnd;
  }

  return out;
}

function sortAndMergeFragmentsOnNode(
  plan: DocumentLinkInjectionPlan
): DocumentLinkInjectionPlan {
  const next = new Map<number, TextNodeInjectionFragment[]>();
  for (const [k, frags] of plan) {
    const sorted = [...frags].sort((a, b) => a.start - b.start);
    next.set(k, sorted);
  }
  return next;
}

function apiSnapshot(
  link: DocumentLink,
  currentDocumentId?: string
): DocumentLinkApiSnapshot {
  const eff = getEffectiveDivFields(link, currentDocumentId);
  return {
    linkId: link.id,
    uniqueMatchText: eff.uniqueMatchText,
    uniqueChunkText: eff.uniqueChunkText,
    primaryHtmlId: eff.htmlIds?.[0],
  };
}

export interface DocumentLinkInjectionResult {
  plan: DocumentLinkInjectionPlan;
  report: DocumentLinkInjectionDebugReport;
}

/**
 * Cache for {@link buildDocumentLinkInjectionPlan}. This computation is expensive
 * (DOMParser + per-link ancestor walk over all text nodes — hundreds of ms for docs
 * with thousands of links). It is a pure function of (documentLinksMap, currentDocumentId,
 * safeHtml), so results are memoized across re-renders / StrictMode double-invokes.
 *
 * Keyed first by the `documentLinksMap` reference (WeakMap → auto-evicts when a document's
 * links are dropped), then by `currentDocumentId + safeHtml`.
 */
const injectionPlanCache = new WeakMap<
  Map<string, DocumentLink>,
  Map<string, DocumentLinkInjectionResult>
>();

const INJECTION_PLAN_CACHE_MAX_PER_MAP = 4;

/**
 * The one result for a document without links. Shared so the parser memo in
 * DocumentRenderer sees a stable plan when an empty link list arrives, instead
 * of re-parsing the whole document (LAR-487).
 */
const EMPTY_INJECTION_RESULT: DocumentLinkInjectionResult = {
  plan: new Map<number, TextNodeInjectionFragment[]>(),
  report: EMPTY_INJECTION_DEBUG_REPORT,
};

/**
 * Precomputes injected link fragments (merged overlaps, split across blocks / text nodes).
 * Uses the same sanitized HTML string as `parse()` in DocumentRenderer.
 * Results are memoized — see {@link injectionPlanCache}.
 */
export function buildDocumentLinkInjectionPlan(
  safeHtml: string,
  documentLinksMap: Map<string, DocumentLink>,
  currentDocumentId?: string
): DocumentLinkInjectionResult {
  // Nothing to inject: skip the whole-document DOMParser pass and text walk.
  // This must come before the cache, which is keyed by the links map — a
  // shared empty map would otherwise pin full-HTML cache keys forever.
  if (documentLinksMap.size === 0) return EMPTY_INJECTION_RESULT;

  let byKey = injectionPlanCache.get(documentLinksMap);
  if (!byKey) {
    byKey = new Map<string, DocumentLinkInjectionResult>();
    injectionPlanCache.set(documentLinksMap, byKey);
  }
  const cacheKey = `${currentDocumentId ?? ""}\u0000${safeHtml}`;
  const cached = byKey.get(cacheKey);
  if (cached) return cached;

  const result = computeDocumentLinkInjectionPlan(
    safeHtml,
    documentLinksMap,
    currentDocumentId
  );

  // Bound memory: keep only the most recent few HTML variants per links map.
  if (byKey.size >= INJECTION_PLAN_CACHE_MAX_PER_MAP) {
    const oldestKey = byKey.keys().next().value;
    if (oldestKey !== undefined) byKey.delete(oldestKey);
  }
  byKey.set(cacheKey, result);
  return result;
}

function computeDocumentLinkInjectionPlan(
  safeHtml: string,
  documentLinksMap: Map<string, DocumentLink>,
  currentDocumentId?: string
): DocumentLinkInjectionResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(safeHtml, "text/html");
  const ppDoclink = collectPreprocessedDoclinkDebug(doc);
  const preprocessed = ppDoclink.extractedGuids;

  const { nodes: allNodes, elementInterval } = collectTextNodes(doc);

  const injectableIndices: number[] = [];
  for (const n of allNodes) {
    if (!n.skipInjection) {
      injectableIndices.push(n.index);
    }
  }

  const injectableMeta: InjectableMeta[] = [];
  let flatLen = 0;
  const flatBaseByGlobalIndex = new Map<number, number>();

  for (const gi of injectableIndices) {
    const item = allNodes[gi];
    flatBaseByGlobalIndex.set(gi, flatLen);
    injectableMeta.push({
      globalTextNodeIndex: gi,
      text: item.text,
      flatBase: flatLen,
    });
    flatLen += item.text.length;
  }

  const injectableFlatLen = flatLen;
  const injectableFlatString = injectableMeta.map((m) => m.text).join("");

  const seenLinkIds = new Set<string>();
  for (const link of documentLinksMap.values()) {
    if (seenLinkIds.has(link.id)) continue;
    seenLinkIds.add(link.id);
  }

  const sortedLinkIds = Array.from(seenLinkIds).sort();

  const apiSnapshots = sortedLinkIds
    .map((id) => documentLinksMap.get(id))
    .filter((l): l is DocumentLink => Boolean(l))
    .map((l) => apiSnapshot(l, currentDocumentId));

  const skipped = new Map<
    string,
    { reason: DocumentLinkSkipReason; detail?: string }
  >();
  const injectionPath = new Map<string, DocumentLinkInjectionPath>();
  /** When match required widening past `primaryHtmlId` subtree */
  const injectionWidenedDetail = new Map<string, string>();

  const rawRanges: RawRange[] = [];

  for (const linkId of sortedLinkIds) {
    const link = documentLinksMap.get(linkId);
    if (!link) {
      skipped.set(linkId, { reason: "missing_from_map" });
      continue;
    }

    if (preprocessed.has(link.id)) {
      skipped.set(linkId, { reason: "already_in_html" });
      continue;
    }

    const effective = getEffectiveDivFields(link, currentDocumentId);
    const uniqueMatchText = effective.uniqueMatchText;
    const uniqueChunkText = effective.uniqueChunkText;
    const targetHtmlId = effective.htmlIds?.[0];

    if (!uniqueMatchText) {
      skipped.set(linkId, { reason: "no_match_text" });
      continue;
    }

    const chunkForInjection = effectiveChunkForInjection(
      uniqueChunkText,
      uniqueMatchText
    );

    if (!targetHtmlId) {
      skipped.set(linkId, { reason: "no_primary_html_id" });
      continue;
    }

    let syntheticScopeDetail: string | undefined;
    const resolvedScope = tryResolveScopeElementByHtmlId(
      doc,
      targetHtmlId,
      documentLinksMap,
      currentDocumentId
    );
    const scopeEl = resolvedScope?.element ?? null;
    if (resolvedScope?.detail) {
      syntheticScopeDetail = resolvedScope.detail;
    }
    if (!scopeEl) {
      skipped.set(linkId, {
        reason: "target_not_in_dom",
        detail: `no element for primaryHtmlId ${JSON.stringify(targetHtmlId)} (tried id fallbacks + synthetic doclink_grp_*)`,
      });
      continue;
    }

    const MAX_ANCESTOR_HOPS = 48;
    let searchRoot: Element | null = scopeEl;
    let hops = 0;
    let lastNonEmptySubHaystackLen = 0;
    let everHadInjectableTextInScope = false;

    let found: {
      flatStart: number;
      flatEnd: number;
      path: DocumentLinkInjectionPath;
    } | null = null;

    while (hops < MAX_ANCESTOR_HOPS) {
      const root = searchRoot;

      const range = scopeIndexRange(allNodes, elementInterval.get(root));
      const orderedScopeInjectable = range
        ? sliceSortedInRange(injectableIndices, range.lo, range.hi)
        : [];
      if (orderedScopeInjectable.length > 0) {
        everHadInjectableTextInScope = true;
        lastNonEmptySubHaystackLen = orderedScopeInjectable
          .map((idx) => allNodes[idx].text)
          .join("").length;
      }

      const attempt = tryFindInSearchRoot(
        root,
        allNodes,
        injectableIndices,
        uniqueMatchText,
        chunkForInjection,
        targetHtmlId,
        flatBaseByGlobalIndex,
        injectableFlatLen,
        elementInterval
      );

      if (attempt) {
        const path: DocumentLinkInjectionPath =
          root === scopeEl ? attempt.path : toExpandedPath(attempt.path);
        found = {
          flatStart: attempt.flatStart,
          flatEnd: attempt.flatEnd,
          path,
        };
        if (root !== scopeEl) {
          const widened = `scopeWidened ${describeElementForDebug(scopeEl)}→${describeElementForDebug(root)}`;
          injectionWidenedDetail.set(
            linkId,
            syntheticScopeDetail
              ? `${syntheticScopeDetail}; ${widened}`
              : widened
          );
        } else if (syntheticScopeDetail) {
          injectionWidenedDetail.set(linkId, syntheticScopeDetail);
        }
        break;
      }

      const next = root.parentElement;
      if (!next || next.nodeName === "HTML") break;
      searchRoot = next;
      hops += 1;
    }

    if (!found) {
      if (!everHadInjectableTextInScope) {
        skipped.set(linkId, { reason: "no_injectable_text_in_scope" });
      } else {
        skipped.set(linkId, {
          reason: "not_found_in_subtree",
          detail: `exhausted ancestor scopes (max ${MAX_ANCESTOR_HOPS} hops); lastSubHaystackLen=${lastNonEmptySubHaystackLen} hops=${hops}`,
        });
      }
      continue;
    }

    rawRanges.push({
      flatStart: found.flatStart,
      flatEnd: found.flatEnd,
      links: [linkEntryFor(link)],
    });
    injectionPath.set(linkId, found.path);
  }

  const rawRangesBeforeMerge: DocumentLinkRawRangeDebug[] = rawRanges.map(
    (r) => ({
      linkId: r.links[0]!.linkId,
      flatStart: r.flatStart,
      flatEnd: r.flatEnd,
      textAtRange: injectableFlatString.slice(r.flatStart, r.flatEnd),
    })
  );

  const merged = mergeOverlappingRanges(rawRanges);

  const plan: DocumentLinkInjectionPlan = new Map();
  let groupIndex = 0;

  const mergedGroupsDebug: DocumentLinkMergedGroupDebug[] = [];

  for (const mrg of merged) {
    const pieces = flatRangeToFragments(
      mrg.flatStart,
      mrg.flatEnd,
      injectableMeta,
      mrg.links,
      groupIndex
    );

    const segDebug: DocumentLinkInjectionSegmentDebug[] = pieces.map((p) => ({
      triggerId: p.triggerId,
      textNodeIndex: p.globalTextNodeIndex,
      localStart: p.start,
      localEnd: p.end,
      matchText: p.matchText,
    }));

    mergedGroupsDebug.push({
      groupIndex,
      merged: mrg.links.length > 1,
      linkIds: mrg.links.map((l) => l.linkId),
      flatStart: mrg.flatStart,
      flatEnd: mrg.flatEnd,
      segments: segDebug,
    });

    groupIndex += 1;

    for (const p of pieces) {
      const { globalTextNodeIndex, ...frag } = p;
      const existing = plan.get(globalTextNodeIndex) ?? [];
      existing.push(frag);
      plan.set(globalTextNodeIndex, existing);
    }
  }

  const linkToGroup = new Map<string, number>();
  for (const g of mergedGroupsDebug) {
    for (const id of g.linkIds) {
      linkToGroup.set(id, g.groupIndex);
    }
  }

  const perLink: DocumentLinkPerLinkOutcome[] = sortedLinkIds.map((id) => {
    const link = documentLinksMap.get(id);
    const api = link
      ? apiSnapshot(link, currentDocumentId)
      : {
          linkId: id,
          uniqueMatchText: undefined,
          uniqueChunkText: undefined,
          primaryHtmlId: undefined,
        };

    const sk = skipped.get(id);
    if (sk) {
      return {
        linkId: id,
        api,
        status: "skipped" as const,
        skipReason: sk.reason,
        detail: sk.detail,
      };
    }

    const gIdx = linkToGroup.get(id);
    if (gIdx === undefined) {
      return {
        linkId: id,
        api,
        status: "skipped" as const,
        skipReason: "not_found_in_subtree",
        detail: "expected injection but link not in merged groups",
      };
    }

    const g = mergedGroupsDebug[gIdx];
    const widened = injectionWidenedDetail.get(id);
    return {
      linkId: id,
      api,
      status: "injected" as const,
      groupIndex: gIdx,
      mergedWithOthers: g.merged,
      injectionPath: injectionPath.get(id),
      ...(widened ? { detail: widened } : {}),
    };
  });

  const report: DocumentLinkInjectionDebugReport = {
    apiLinkIds: sortedLinkIds,
    apiSnapshots,
    perLink,
    mergedGroups: mergedGroupsDebug,
    injectableFlatLength: injectableFlatLen,
    totalTextNodeCount: allNodes.length,
    preprocessedLinkIdsFromHtml: Array.from(ppDoclink.extractedGuids).sort(),
    preprocessedDoclinkAnchorIds: ppDoclink.anchorIdAttributes,
    rawRangesBeforeMerge,
  };

  return {
    plan: sortAndMergeFragmentsOnNode(plan),
    report,
  };
}
